import Mention from '@tiptap/extension-mention'
import type { SuggestionOptions } from '@tiptap/suggestion'

export interface MentionCharacter {
  id: string
  name: string
}

function createSuggestionRenderer(): NonNullable<SuggestionOptions['render']> {
  return () => {
    let popup: HTMLDivElement | null = null
    let items: MentionCharacter[] = []
    let selectedIndex = 0
    let command: ((props: { id: string; label: string }) => void) | null = null

    function updatePopup() {
      if (!popup) return
      popup.innerHTML = ''
      if (items.length === 0) {
        popup.style.display = 'none'
        return
      }
      popup.style.display = 'block'
      items.forEach((item, i) => {
        const row = document.createElement('div')
        row.className = `mention-suggestion-item${i === selectedIndex ? ' is-selected' : ''}`
        row.textContent = item.name
        row.addEventListener('mousedown', (e) => {
          e.preventDefault()
          command?.({ id: item.id, label: item.name })
        })
        row.addEventListener('mouseenter', () => {
          selectedIndex = i
          updatePopup()
        })
        popup!.appendChild(row)
      })
    }

    return {
      onStart(props) {
        popup = document.createElement('div')
        popup.className = 'mention-suggestion-popup'
        popup.style.display = 'none'
        document.body.appendChild(popup)

        items = props.items as MentionCharacter[]
        selectedIndex = 0
        command = (attrs) => props.command(attrs)

        const rect = props.clientRect?.()
        if (rect && popup) {
          popup.style.left = `${rect.left}px`
          popup.style.top = `${rect.bottom + 4}px`
        }
        updatePopup()
      },
      onUpdate(props) {
        items = props.items as MentionCharacter[]
        selectedIndex = 0
        command = (attrs) => props.command(attrs)

        const rect = props.clientRect?.()
        if (rect && popup) {
          popup.style.left = `${rect.left}px`
          popup.style.top = `${rect.bottom + 4}px`
        }
        updatePopup()
      },
      onKeyDown({ event }) {
        if (event.key === 'ArrowUp') {
          selectedIndex = (selectedIndex - 1 + items.length) % items.length
          updatePopup()
          return true
        }
        if (event.key === 'ArrowDown') {
          selectedIndex = (selectedIndex + 1) % items.length
          updatePopup()
          return true
        }
        if (event.key === 'Enter') {
          const item = items[selectedIndex]
          if (item) command?.({ id: item.id, label: item.name })
          return true
        }
        if (event.key === 'Escape') {
          popup?.remove()
          popup = null
          return true
        }
        return false
      },
      onExit() {
        popup?.remove()
        popup = null
      },
    }
  }
}

export function createCharacterMention(characters: MentionCharacter[]) {
  return Mention.configure({
    HTMLAttributes: {
      class: 'character-mention',
    },
    suggestion: {
      char: '@',
      allowSpaces: false,
      items: ({ query }) => {
        const q = query.toLowerCase()
        return characters.filter((c) =>
          c.name.toLowerCase().includes(q),
        )
      },
      render: createSuggestionRenderer(),
    },
    renderLabel({ node }) {
      return `@${node.attrs.label ?? node.attrs.id}`
    },
  })
}
